import { useEffect, useState } from "react";

import { formatChinaDateTime } from "../time";
import {
  collectionEntriesExportUrl,
  listCollectionEntries,
  PLATFORM_TOKEN_KEY,
  type CollectionAccountType,
  type CollectionEntry,
  type CollectionMethod,
} from "./api";

const PAGE_SIZE = 10;

const money = (value: string) => `¥${Number(value).toLocaleString("zh-CN", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})}`;

function accountTypeLabel(value: CollectionAccountType): string {
  return value === "PERSONAL_LAWYER" ? "个人律师" : "机构账户";
}

function methodLabel(value: CollectionMethod): string {
  return { WECHAT: "微信支付", ALIPAY: "支付宝", MANUAL: "人工充值" }[value];
}

export function PaymentCollections() {
  const [items, setItems] = useState<CollectionEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [draftQuery, setDraftQuery] = useState("");
  const [accountType, setAccountType] = useState<CollectionAccountType | "">("");
  const [method, setMethod] = useState<CollectionMethod | "">("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");

  const filters = { query, accountType, method, dateFrom, dateTo };

  async function reload() {
    setLoading(true);
    setError("");
    try {
      const result = await listCollectionEntries({ ...filters, page, pageSize: PAGE_SIZE });
      setItems(result.items);
      setTotal(result.total);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "收款明细加载失败");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
  }, [page, query, accountType, method, dateFrom, dateTo]);

  function search(event: React.FormEvent) {
    event.preventDefault();
    setPage(1);
    setQuery(draftQuery.trim());
  }

  function resetFilters() {
    setDraftQuery("");
    setQuery("");
    setAccountType("");
    setMethod("");
    setDateFrom("");
    setDateTo("");
    setPage(1);
  }

  async function download() {
    if (exporting) return;
    setExporting(true);
    setError("");
    try {
      const token = sessionStorage.getItem(PLATFORM_TOKEN_KEY);
      const response = await fetch(collectionEntriesExportUrl(filters), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) throw new Error("收款明细下载失败，请稍后重试");
      const link = document.createElement("a");
      link.href = URL.createObjectURL(await response.blob());
      link.download = "收款明细.xlsx";
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(link.href), 0);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "收款明细下载失败，请稍后重试");
    } finally {
      setExporting(false);
    }
  }

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return <section className="platform-payment-collections" aria-label="收款明细">
    <div className="platform-panel">
      <form className="platform-list-toolbar payment-collection-toolbar" onSubmit={search}>
        <div className="platform-list-search">
          <input aria-label="搜索收款明细" placeholder="搜索账户名称、账户编号或流水号" value={draftQuery} onChange={(event) => setDraftQuery(event.target.value)} />
          <select aria-label="账户类型" value={accountType} onChange={(event) => { setPage(1); setAccountType(event.target.value as CollectionAccountType | ""); }}><option value="">全部账户类型</option><option value="PERSONAL_LAWYER">个人律师</option><option value="INSTITUTION">机构账户</option></select>
          <select aria-label="充值方式" value={method} onChange={(event) => { setPage(1); setMethod(event.target.value as CollectionMethod | ""); }}><option value="">全部充值方式</option><option value="WECHAT">微信支付</option><option value="ALIPAY">支付宝</option><option value="MANUAL">人工充值</option></select>
          <label>开始日期<input type="date" value={dateFrom} onChange={(event) => { setPage(1); setDateFrom(event.target.value); }} /></label>
          <label>结束日期<input type="date" value={dateTo} onChange={(event) => { setPage(1); setDateTo(event.target.value); }} /></label>
          <button type="submit">查询</button>
          <button className="is-secondary" type="button" onClick={resetFilters}>重置</button>
          <button className="is-secondary" type="button" disabled={exporting} onClick={() => void download()}>{exporting ? "下载中…" : "下载明细"}</button>
        </div>
      </form>
      {error && <p className="platform-inline-error">{error}</p>}
      <div className="platform-table-wrap"><table className="platform-account-table payment-collection-table"><thead><tr><th>到账时间</th><th>账户类型</th><th>充值对象</th><th>账户编号</th><th>流水号</th><th>充值方式</th><th>金额</th><th>操作信息</th></tr></thead><tbody>
        {!loading && items.length === 0 ? <tr><td colSpan={8}>暂无充值流水</td></tr> : items.map((item) => <tr key={`${item.account_type}-${item.id}`}><td>{formatChinaDateTime(item.received_at)}</td><td>{accountTypeLabel(item.account_type)}</td><td><strong>{item.account_name}</strong></td><td>{item.account_number}</td><td>{item.reference}</td><td>{methodLabel(item.method)}</td><td>{money(item.amount)}</td><td>{item.operator_name}<small>{item.note || "—"}</small></td></tr>)}
        {loading && <tr><td colSpan={8}>正在加载收款明细…</td></tr>}
      </tbody></table></div>
      <footer className="platform-pagination"><span>共 {total} 条，每页 {PAGE_SIZE} 条</span><div><button type="button" disabled={page <= 1 || loading} onClick={() => setPage((value) => value - 1)}>上一页</button><span>{page} / {pages}</span><button type="button" disabled={page >= pages || loading} onClick={() => setPage((value) => value + 1)}>下一页</button></div></footer>
    </div>
  </section>;
}
